import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { PromotersTab } from "@/components/admin/PromotersTab";
import { PinsTab } from "@/components/admin/PinsTab";
import { ContestTab } from "@/components/admin/ContestTab";
import { CatalogTab } from "@/components/admin/CatalogTab";
import { TelegramTab } from "@/components/admin/TelegramTab";
import { BusinessesTab } from "@/components/admin/BusinessesTab";
import { JudgesTab } from "@/components/admin/JudgesTab";
import { CheckCircle, XCircle, Eye, Receipt, MoneyWavy, ChartLineUp, Coins, Bell, TelegramLogo, MusicNotesSimple, Handshake, Key, Trophy, Storefront, Gavel } from "@phosphor-icons/react";

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("bbva");
  const [stats, setStats] = useState(null);
  const [bbvaList, setBbvaList] = useState([]);
  const [stripeList, setStripeList] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewReceipt, setViewReceipt] = useState(null);
  const prevReviewCount = useRef(0);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    if (!user.is_admin) { nav("/"); return; }
    refresh();
    const t = setInterval(refresh, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, nav]);

  // Play subtle ping when new receipt arrives
  useEffect(() => {
    if (!alerts) return;
    const c = alerts.review_count || 0;
    if (c > prevReviewCount.current && prevReviewCount.current !== 0) {
      audioRef.current?.play?.().catch(() => {});
      toast.success("📩 Nuevo comprobante por revisar");
    }
    prevReviewCount.current = c;
  }, [alerts]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, b, st, a] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/transactions/bbva"),
        api.get("/admin/transactions/stripe"),
        api.get("/admin/alerts"),
      ]);
      setStats(s.data);
      setBbvaList(b.data);
      setStripeList(st.data);
      setAlerts(a.data);
    } catch (e) {
      toast.error("Error cargando datos");
    } finally { setLoading(false); }
  };

  const confirmBbva = async (tx_id) => {
    if (!window.confirm("¿Confirmar este pago? La canción se activará para el usuario.")) return;
    try {
      await api.post("/admin/transactions/bbva/confirm", { transaction_id: tx_id });
      toast.success("Pago confirmado, canción activada");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error confirmando");
    }
  };

  const rejectBbva = async (tx_id) => {
    const note = window.prompt("Motivo del rechazo (opcional):", "");
    if (note === null) return;
    try {
      await api.post("/admin/transactions/bbva/reject", { transaction_id: tx_id, note });
      toast.success("Transferencia rechazada");
      refresh();
    } catch (e) { toast.error("Error rechazando"); }
  };

  const openReceipt = async (tx_id) => {
    try {
      const { data } = await api.get(`/admin/transactions/${tx_id}/receipt`);
      setViewReceipt({ tx_id, image: data.image });
    } catch (e) { toast.error("No hay comprobante"); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" });
  };

  const statusBadge = (s) => {
    const map = {
      awaiting_transfer: { bg: "bg-white/5", t: "text-white/60", label: "Esperando" },
      receipt_uploaded: { bg: "bg-[#FFB800]/15", t: "text-[#FFB800]", label: "Comprobante subido" },
      pending_review: { bg: "bg-[#FFB800]/15", t: "text-[#FFB800]", label: "Por revisar" },
      paid: { bg: "bg-[#B4F34A]/15", t: "text-[#B4F34A]", label: "Pagado" },
      rejected: { bg: "bg-[#FF4B00]/15", t: "text-[#FF4B00]", label: "Rechazado" },
      initiated: { bg: "bg-white/5", t: "text-white/60", label: "Iniciado" },
      expired: { bg: "bg-white/5", t: "text-white/40", label: "Expirado" },
    };
    const m = map[s] || { bg: "bg-white/5", t: "text-white/60", label: s };
    return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${m.bg} ${m.t}`}>{m.label}</span>;
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm uppercase tracking-widest text-[#FF4B00] mb-2">Panel administrador</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold">Pagos & transferencias</h1>
          </div>
          <button onClick={refresh} className="px-3 py-1.5 text-xs rounded-full bg-white/5 hover:bg-white/10 text-white/70" data-testid="admin-refresh">
            {loading ? "Actualizando…" : "Actualizar"}
          </button>
        </div>

        {/* Alert banner: payments waiting review */}
        {alerts?.review_count > 0 && (
          <div className="rounded-2xl border-2 border-[#FFB800] bg-[#FFB800]/10 p-5 mb-6 flex items-start gap-4 animate-pulse-slow" data-testid="alert-banner">
            <div className="w-12 h-12 rounded-full bg-[#FFB800] text-black flex items-center justify-center shrink-0">
              <Bell weight="fill" size={22} />
            </div>
            <div className="flex-1">
              <p className="font-display font-bold text-lg">
                {alerts.review_count} pago{alerts.review_count > 1 ? 's' : ''} por revisar
              </p>
              {alerts.latest_pending && (
                <p className="text-sm text-white/70">
                  Último: <span className="text-white font-medium">{alerts.latest_pending.user?.name}</span> ·{" "}
                  <span className="text-[#FFB800] font-mono">{alerts.latest_pending.user?.phone}</span> ·{" "}
                  Ref <span className="font-mono">{alerts.latest_pending.reference}</span> ·{" "}
                  <span className="capitalize">{alerts.latest_pending.purpose}</span>
                </p>
              )}
            </div>
            <button
              onClick={() => setTab("bbva")}
              className="px-4 py-2 rounded-full bg-[#FFB800] hover:bg-[#E6A600] text-black font-semibold text-sm"
            >
              Revisar
            </button>
          </div>
        )}

        {/* Telegram status pill */}
        {alerts && (
          <div className="mb-6 flex items-center gap-2 text-xs">
            <TelegramLogo size={16} weight="fill" className={alerts.telegram_configured ? "text-[#26A5E4]" : "text-white/30"} />
            <span className="text-white/50">
              Alertas Telegram:{" "}
              {alerts.telegram_configured ? (
                <span className="text-[#B4F34A] font-semibold">activas</span>
              ) : (
                <span className="text-white/40">no configuradas</span>
              )}
            </span>
            <button
              onClick={() => setTab("telegram")}
              className="ml-2 text-[#26A5E4] hover:underline font-semibold"
              data-testid="tab-telegram-shortcut"
            >
              {alerts.telegram_configured ? "Configurar" : "Configurar ahora →"}
            </button>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="admin-stats">
            <StatCard icon={MoneyWavy} label="Ingresos" value={`$${stats.revenue_mxn} MXN`} accent="#B4F34A" />
            <StatCard icon={Coins} label="Pagados BBVA" value={stats.paid_bbva} accent="#FFB800" />
            <StatCard icon={Receipt} label="BBVA pendientes" value={stats.pending_bbva} accent="#FF4B00" />
            <StatCard icon={ChartLineUp} label="Usuarios" value={stats.total_users || 0} accent="#FFFFFF" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/5" data-testid="admin-tabs">
          <button
            onClick={() => setTab("bbva")}
            data-testid="tab-bbva"
            className={`px-4 py-3 text-sm font-display font-bold border-b-2 transition-colors ${tab === "bbva" ? 'border-[#FFB800] text-white' : 'border-transparent text-white/50 hover:text-white/80'}`}
          >
            Transferencias BBVA
          </button>
          <button
            onClick={() => setTab("stripe")}
            data-testid="tab-stripe"
            className={`px-4 py-3 text-sm font-display font-bold border-b-2 transition-colors ${tab === "stripe" ? 'border-[#FFB800] text-white' : 'border-transparent text-white/50 hover:text-white/80'}`}
          >
            Pagos Stripe
          </button>
          <button
            onClick={() => setTab("telegram")}
            data-testid="tab-telegram"
            className={`px-4 py-3 text-sm font-display font-bold border-b-2 transition-colors flex items-center gap-1.5 ${tab === "telegram" ? 'border-[#FFB800] text-white' : 'border-transparent text-white/50 hover:text-white/80'}`}
          >
            <TelegramLogo size={14} weight="fill" /> Telegram
          </button>
          <button
            onClick={() => setTab("catalog")}
            data-testid="tab-catalog"
            className={`px-4 py-3 text-sm font-display font-bold border-b-2 transition-colors flex items-center gap-1.5 ${tab === "catalog" ? 'border-[#FFB800] text-white' : 'border-transparent text-white/50 hover:text-white/80'}`}
          >
            <MusicNotesSimple size={14} weight="fill" /> Catálogo
          </button>
          <button
            onClick={() => setTab("promoters")}
            data-testid="tab-promoters"
            className={`px-4 py-3 text-sm font-display font-bold border-b-2 transition-colors flex items-center gap-1.5 ${tab === "promoters" ? 'border-[#FFB800] text-white' : 'border-transparent text-white/50 hover:text-white/80'}`}
          >
            <Handshake size={14} weight="fill" /> Promotores
          </button>
          <button
            onClick={() => setTab("pins")}
            data-testid="tab-pins"
            className={`px-4 py-3 text-sm font-display font-bold border-b-2 transition-colors flex items-center gap-1.5 ${tab === "pins" ? 'border-[#FFB800] text-white' : 'border-transparent text-white/50 hover:text-white/80'}`}
          >
            <Key size={14} weight="fill" /> PINs
          </button>
          <button
            onClick={() => setTab("contest")}
            data-testid="tab-contest"
            className={`px-4 py-3 text-sm font-display font-bold border-b-2 transition-colors flex items-center gap-1.5 ${tab === "contest" ? 'border-[#FFB800] text-white' : 'border-transparent text-white/50 hover:text-white/80'}`}
          >
            <Trophy size={14} weight="fill" /> Concurso
          </button>
          <button
            onClick={() => setTab("businesses")}
            data-testid="tab-businesses"
            className={`px-4 py-3 text-sm font-display font-bold border-b-2 transition-colors flex items-center gap-1.5 ${tab === "businesses" ? 'border-[#FFB800] text-white' : 'border-transparent text-white/50 hover:text-white/80'}`}
          >
            <Storefront size={14} weight="fill" /> Negocios
          </button>
          <button
            onClick={() => setTab("judges")}
            data-testid="tab-judges"
            className={`px-4 py-3 text-sm font-display font-bold border-b-2 transition-colors flex items-center gap-1.5 ${tab === "judges" ? 'border-[#FFB800] text-white' : 'border-transparent text-white/50 hover:text-white/80'}`}
          >
            <Gavel size={14} weight="fill" /> Jueces
          </button>
        </div>

        {/* BBVA list */}
        {tab === "bbva" && (
          <div className="space-y-2" data-testid="bbva-tx-list">
            {bbvaList.length === 0 && <p className="text-white/40 text-center py-12">No hay transferencias BBVA aún.</p>}
            {bbvaList.map(tx => (
              <div key={tx.id} className="rounded-2xl bg-[#121212] border border-white/5 p-4" data-testid={`bbva-tx-${tx.id}`}>
                <div className="flex flex-wrap items-start gap-3">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="font-display font-bold">
                        {tx.purpose === "credit"
                          ? <span className="flex items-center gap-1.5"><Coins size={16} weight="fill" className="text-[#FFB800]" /> Compra de crédito</span>
                          : (tx.song?.title || "Sin título")}
                      </p>
                      {statusBadge(tx.status)}
                    </div>
                    {tx.purpose !== "credit" && (
                      <p className="text-sm text-white/60">{tx.song?.artist_name} · <span className="capitalize">{(tx.song?.genre || "").replace(/_/g, ' ')}</span></p>
                    )}
                    <p className="text-xs text-white/40 mt-2">
                      Usuario: <span className="text-white/70">{tx.user?.name}</span> · {tx.user?.phone}
                    </p>
                    <p className="text-xs text-white/40">Ref <span className="font-mono">{tx.reference || tx.id.slice(0, 8).toUpperCase()}</span> · {fmtDate(tx.created_at)}</p>
                    {tx.receipt_uploaded_at && <p className="text-xs text-[#FFB800]">📩 Comprobante: {fmtDate(tx.receipt_uploaded_at)}</p>}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                    <p className="font-display font-bold text-lg whitespace-nowrap">${tx.amount} MXN</p>
                    {tx.has_receipt && (
                      <button onClick={() => openReceipt(tx.id)} className="px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 text-xs flex items-center gap-1.5" data-testid={`view-receipt-${tx.id}`}>
                        <Eye size={14} /> Ver
                      </button>
                    )}
                    {tx.status !== "paid" && tx.status !== "rejected" && (
                      <>
                        <button
                          onClick={() => confirmBbva(tx.id)}
                          className="px-3 py-2 rounded-full bg-[#B4F34A] hover:bg-[#A0E03A] text-black text-xs font-semibold flex items-center gap-1.5"
                          data-testid={`confirm-${tx.id}`}
                        >
                          <CheckCircle weight="fill" size={14} /> Confirmar
                        </button>
                        <button
                          onClick={() => rejectBbva(tx.id)}
                          className="px-3 py-2 rounded-full bg-white/5 hover:bg-[#FF4B00]/20 text-xs text-white/70 flex items-center gap-1.5"
                          data-testid={`reject-${tx.id}`}
                        >
                          <XCircle size={14} /> Rechazar
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {tx.admin_note && (
                  <p className="mt-3 text-xs text-white/50 italic">Nota: {tx.admin_note}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Stripe list (read-only) */}
        {tab === "stripe" && (
          <div className="space-y-2" data-testid="stripe-tx-list">
            {stripeList.length === 0 && <p className="text-white/40 text-center py-12">No hay pagos Stripe aún.</p>}
            {stripeList.map(tx => (
              <div key={tx.id} className="rounded-2xl bg-[#121212] border border-white/5 p-4" data-testid={`stripe-tx-${tx.id}`}>
                <div className="flex flex-wrap items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-display font-bold">{tx.song?.title || "Sin título"}</p>
                      {statusBadge(tx.status)}
                    </div>
                    <p className="text-sm text-white/60">{tx.song?.artist_name}</p>
                    <p className="text-xs text-white/40 mt-2">
                      Usuario: {tx.user?.name} · {tx.user?.phone}
                    </p>
                    <p className="text-xs text-white/40 font-mono">Session: {tx.session_id?.slice(0, 24)}...</p>
                    <p className="text-xs text-white/40">{fmtDate(tx.created_at)}</p>
                  </div>
                  <p className="font-display font-bold text-lg">${tx.amount} MXN</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* TELEGRAM SETUP TAB */}
        {tab === "telegram" && (
          <TelegramTab alerts={alerts} onRefresh={refresh} />
        )}

        {/* PUBLIC CATALOG TAB */}
        {tab === "catalog" && (
          <CatalogTab />
        )}

        {/* PROMOTERS TAB */}
        {tab === "promoters" && (
          <PromotersTab />
        )}

        {/* ACCESS PINS TAB */}
        {tab === "pins" && (
          <PinsTab />
        )}

        {/* CONTEST TAB */}
        {tab === "contest" && (
          <ContestTab />
        )}

        {/* BUSINESSES TAB */}
        {tab === "businesses" && (
          <BusinessesTab />
        )}

        {/* JUDGES TAB */}
        {tab === "judges" && (
          <JudgesTab />
        )}
      </div>

      {/* Receipt modal */}
      {viewReceipt && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setViewReceipt(null)}
          data-testid="receipt-modal"
        >
          <div className="max-w-2xl max-h-[90vh] overflow-auto rounded-2xl bg-[#121212] p-2" onClick={(e) => e.stopPropagation()}>
            <img src={viewReceipt.image} alt="Comprobante" className="w-full rounded-xl" />
            <button onClick={() => setViewReceipt(null)} className="mt-2 w-full py-3 rounded-full bg-white/5 hover:bg-white/10 text-sm">Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl bg-[#121212] border border-white/5 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} style={{ color: accent }} />
        <p className="text-[10px] uppercase tracking-widest text-white/50">{label}</p>
      </div>
      <p className="font-display text-2xl font-black">{value}</p>
    </div>
  );
}
